/**
 * Transformation-row substrate queries for Atlas Day 3a Phase 1.
 *
 * Single async function fetches all transformation-row substrate counts +
 * derived metrics for Decision rules cluster sub-region cards.
 *
 * Substrate per Atlas Day 3a Phase 0 verifications:
 *   - distributors with credit_limit > 0 + payment_terms range (Credit gate)
 *   - brands.enforces_map count vs total active brands (MAP enforcement)
 *   - fpc.is_active count × marketplaces.is_active count (Path eligibility)
 *   - bswc active labels with sort_order (Buy Score composition; D1 (α)
 *     catalog-only per Atlas Day 2 lock — no weight values)
 *
 * Restock urgency tiers reads RPC-default values directly (Red = 1.0×,
 * Yellow = 1.5×) per Sol D-2 lock 2026-05-27 — no DB-stored params at
 * current substrate state; surface line hard-codes RPC-default values.
 *
 * Uses arbi-dev's own supabase client. No caching at Day 3a; direct
 * fetch on cluster mount.
 */

import { supabase } from '../supabase';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MapEnforcementSubstrate {
  enforcesMapCount: number;
  totalActiveBrands: number;
}

export interface CreditGateSubstrate {
  extendsCreditCount: number;
  totalActiveDistributors: number;
  paymentTermsRange: { min: number; max: number } | null;
}

export interface PathEligibilitySubstrate {
  activePathCount: number;
  activeMarketplaceCount: number;
  combinationCount: number;
}

export interface BuyScoreCompositionSubstrate {
  activeBswcLabels: string[];
}

export interface TransformationRowSubstrate {
  mapEnforcement: MapEnforcementSubstrate;
  creditGate: CreditGateSubstrate;
  pathEligibility: PathEligibilitySubstrate;
  buyScoreComposition: BuyScoreCompositionSubstrate;
}

// ── Fetcher ──────────────────────────────────────────────────────────────────

export async function fetchTransformationRowSubstrate(): Promise<TransformationRowSubstrate> {
  const [brEnf, brActive, distActive, fpcActive, mkActive, bswcActive] = await Promise.all([
    supabase.from('brands').select('id', { count: 'exact', head: true }).eq('enforces_map', true),
    supabase.from('brands').select('id', { count: 'exact', head: true }).eq('is_active', true),
    supabase
      .from('distributors')
      .select('credit_limit, payment_terms')
      .eq('is_active', true),
    supabase
      .from('fulfillment_path_config')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    supabase
      .from('marketplaces')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    supabase
      .from('buy_score_weight_components')
      .select('label, sort_order')
      .eq('is_available', true)
      .order('sort_order', { ascending: true }),
  ]);

  const distRows = (distActive.data ?? []) as Array<{
    credit_limit: number | null;
    payment_terms: number | null;
  }>;
  const extendsCreditCount = distRows.filter((r) => (r.credit_limit ?? 0) > 0).length;
  const totalActiveDistributors = distRows.length;
  const paymentTerms = distRows
    .map((r) => r.payment_terms)
    .filter((v): v is number => v != null);
  const paymentTermsRange =
    paymentTerms.length > 0
      ? { min: Math.min(...paymentTerms), max: Math.max(...paymentTerms) }
      : null;

  const activePathCount = fpcActive.count ?? 0;
  const activeMarketplaceCount = mkActive.count ?? 0;

  const activeBswcLabels = ((bswcActive.data ?? []) as Array<{ label: string }>).map(
    (r) => r.label
  );

  return {
    mapEnforcement: {
      enforcesMapCount: brEnf.count ?? 0,
      totalActiveBrands: brActive.count ?? 0,
    },
    creditGate: {
      extendsCreditCount,
      totalActiveDistributors,
      paymentTermsRange,
    },
    pathEligibility: {
      activePathCount,
      activeMarketplaceCount,
      combinationCount: activePathCount * activeMarketplaceCount,
    },
    buyScoreComposition: {
      activeBswcLabels,
    },
  };
}
